"use client";

import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import {
  ApiConfig,
  API_ORDER_ROUTER,
  API_SERVICE_TYPE_MAPS_ROUTER,
  API_SERVICE_ADDON_ROUTER,
} from "../../../services/api-config";
import IconSearch from "../../../components/icons/icon-search";
import { removeVietnameseFromString } from "../../../utils/helpers";
import { ORDER_STATUS } from "../services/order-service";
import { ITEM_TYPE, PickedItem } from "./BranchItemsPicker";

/**
 * Sửa SẢN PHẨM / DỊCH VỤ của đơn trong form cập nhật (đơn chưa hoàn tất, KHÔNG đổi chi nhánh):
 * thêm dòng, bỏ dòng, đổi số lượng.
 *
 * Gửi lên là danh sách ĐẦY ĐỦ sau khi sửa; BE tự so với dòng hiện có (`planItemsChange`):
 *  - dòng giữ lại: giữ nguyên đơn giá đã chốt, chỉ đổi số lượng;
 *  - dòng thêm mới: BE tra giá theo bảng giá chi nhánh + giá riêng của khách (số hiện ở đây là giá bảng);
 *  - dòng bỏ: xoá khỏi đơn.
 * Chỉ báo `dirty` khi có thay đổi thật → form không gửi `items` nếu người dùng không đụng vào.
 */

interface Row {
  key: string;
  item_id: number;
  item_type: number;
  name: string;
  unit: string;
  price: number;
  quantity: string;
  isNew: boolean;
}

interface Option {
  value: string;
  id: number;
  item_type: number;
  label: string;
  codes: string;
  unit: string;
  price: number;
}

interface Props {
  orderId: number;
  branchId: number;
  onChange: (items: PickedItem[], ready: boolean, dirty: boolean) => void;
}

const keyOf = (itemType: number, itemId: number) => `${itemType}_${itemId}`;
const normalizeSearch = (v: string) => removeVietnameseFromString(v || "").toLowerCase();
const isValidQuantity = (v: string) => v.trim() !== "" && !isNaN(Number(v)) && Number(v) >= 0;

const OrderItemsEditor: React.FC<Props> = ({ orderId, branchId, onChange }) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  // Ảnh chụp dòng ban đầu ("type_id" → số lượng) để biết người dùng có sửa gì không.
  const [initial, setInitial] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { request: getOrder } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: getMaps } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });
  const { request: getAddons } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });

  useEffect(() => {
    if (!orderId || !branchId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [orderRes, mapRes, addonRes] = await Promise.all([
          getOrder({ endPoint: API_ORDER_ROUTER.API_DETAIL(orderId) }),
          getMaps({
            endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
          getAddons({
            endPoint: API_SERVICE_ADDON_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
        ]);
        if (!alive) return;

        if (!orderRes || !isSuccess(orderRes)) {
          setError("Không tải được sản phẩm / dịch vụ của đơn");
          return;
        }

        const opts: Option[] = [];
        if (mapRes && isSuccess(mapRes)) {
          for (const m of (mapRes as any)?.data?.list ?? []) {
            const svc = m.service?.name || m.service_name || "";
            const type = m.serviceType?.name || m.service_type?.name || m.service_type_name || "";
            const typeCode = m.serviceType?.code || m.service_type?.code || m.service_type_code || "";
            const svcCode = m.service?.code || m.service_code || "";
            opts.push({
              value: keyOf(ITEM_TYPE.SERVICE, Number(m.id)),
              id: Number(m.id),
              item_type: ITEM_TYPE.SERVICE,
              label: [svc, type].filter(Boolean).join(" - ") || `#${m.id}`,
              codes: [typeCode, svcCode].filter(Boolean).join(" · "),
              unit: m.unit?.name || m.unit_name || "",
              price: Number(m.price) || 0,
            });
          }
        }
        if (addonRes && isSuccess(addonRes)) {
          for (const a of (addonRes as any)?.data?.list ?? []) {
            opts.push({
              value: keyOf(ITEM_TYPE.ADDON, Number(a.id)),
              id: Number(a.id),
              item_type: ITEM_TYPE.ADDON,
              label: a.name || `#${a.id}`,
              codes: a.code || "",
              unit: "",
              price: Number(a.price) || 0,
            });
          }
        }
        setOptions(opts);

        // Dòng đã huỷ (tách sang đơn giặt lại) không thuộc phần sửa — BE cũng bỏ qua khi so.
        const lines: any[] = ((orderRes as any)?.data?.items ?? []).filter(
          (it: any) => Number(it.status) !== ORDER_STATUS.CANCELED,
        );
        const snapshot: Record<string, number> = {};
        setRows(
          lines.map((it) => {
            const key = keyOf(Number(it.item_type), Number(it.item_id));
            snapshot[key] = Number(it.quantity) || 0;
            return {
              key,
              item_id: Number(it.item_id),
              item_type: Number(it.item_type),
              name: String(it.item_name || "—"),
              unit: String(it.unit_name || ""),
              price: Number(it.price) || 0,
              quantity: String(Number(it.quantity) || 0),
              isNew: false,
            };
          }),
        );
        setInitial(snapshot);
      } catch {
        if (alive) setError("Không tải được sản phẩm / dịch vụ của đơn");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [orderId, branchId]);

  // Đẩy kết quả ra ngoài mỗi khi đổi.
  useEffect(() => {
    const picked: PickedItem[] = rows.map((r) => ({
      item_id: r.item_id,
      item_type: r.item_type,
      quantity: Number(r.quantity) || 0,
    }));
    const hasService = rows.some((r) => r.item_type === ITEM_TYPE.SERVICE);
    const ready = !loading && !error && hasService && rows.every((r) => isValidQuantity(r.quantity));
    const dirty =
      rows.length !== Object.keys(initial).length ||
      rows.some((r) => !(r.key in initial) || initial[r.key] !== Number(r.quantity));
    onChange(picked, ready, !loading && !error && dirty);
    // eslint-disable-next-line
  }, [rows, initial, loading, error]);

  const pickedKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);
  const addable = useMemo(() => options.filter((o) => !pickedKeys.has(o.value)), [options, pickedKeys]);

  const total = rows.reduce((s, r) => s + Math.round(r.price * (Number(r.quantity) || 0)), 0);
  const hasService = rows.some((r) => r.item_type === ITEM_TYPE.SERVICE);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h4 className="text-sm font-semibold text-slate-800 mb-3">Sản phẩm / dịch vụ</h4>

      {loading ? (
        <p className="text-sm text-slate-500">Đang tải…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-[1] flex items-center pl-3 text-gray-400">
              <IconSearch className="w-4 h-4" />
            </div>
            <Select
              placeholder="Tìm theo tên hoặc mã để thêm..."
              options={addable}
              // Tìm theo tên HOẶC mã, không phân biệt hoa thường và dấu — giống ô thêm sản phẩm lúc tạo đơn.
              filterOption={(option: any, input: string) => {
                const q = normalizeSearch(input.trim());
                if (!q) return true;
                return normalizeSearch(`${option.data.label} ${option.data.codes}`).includes(q);
              }}
              formatOptionLabel={(option: any) => (
                <div className="flex items-center justify-between gap-3">
                  <span>{option.label}</span>
                  <span className="shrink-0 text-xs text-slate-400">
                    {[option.codes, `${option.price.toLocaleString("vi-VN")}đ`].filter(Boolean).join(" · ")}
                  </span>
                </div>
              )}
              value={null}
              onChange={(o: any) => {
                if (!o) return;
                setRows((prev) =>
                  prev.some((r) => r.key === o.value)
                    ? prev
                    : [
                        ...prev,
                        {
                          key: o.value,
                          item_id: o.id,
                          item_type: o.item_type,
                          name: o.label,
                          unit: o.unit,
                          price: o.price,
                          quantity: "1",
                          isNew: true,
                        },
                      ],
                );
              }}
              isSearchable
              noOptionsMessage={() => "Không tìm thấy sản phẩm / dịch vụ"}
              // Modal có overflow → thả menu ra body để không bị cắt.
              menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
              menuPosition="fixed"
              styles={{
                menuPortal: (base: any) => ({ ...base, zIndex: 60 }),
                // Chừa chỗ cho icon kính lúp bên trái.
                valueContainer: (base: any) => ({ ...base, paddingLeft: 32 }),
              }}
            />
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-slate-500">Đơn chưa có sản phẩm / dịch vụ nào.</p>
          ) : (
            rows.map((r) => (
              <div key={r.key} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-800 truncate" title={r.name}>
                    {r.name}
                    {r.isNew && <span className="ml-2 text-[11px] font-semibold text-green-600">Mới</span>}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {r.price.toLocaleString("vi-VN")}đ{r.unit ? ` / ${r.unit}` : ""}
                  </div>
                </div>
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={r.quantity}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRows((prev) => prev.map((x) => (x.key === r.key ? { ...x, quantity: v } : x)));
                  }}
                  className={`w-20 border rounded-lg px-2 py-1.5 text-sm text-center ${
                    isValidQuantity(r.quantity) ? "" : "border-red-400"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                  className="text-slate-400 hover:text-red-600 px-1"
                  title="Bỏ khỏi đơn"
                >
                  ✕
                </button>
              </div>
            ))
          )}

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-slate-500">
              Dòng thêm mới sẽ áp giá riêng của khách (nếu có) khi lưu.
            </span>
            <span className="text-sm text-slate-700">
              Tạm tính: <b className="text-green-700">{total.toLocaleString("vi-VN")}đ</b>
            </span>
          </div>

          {!hasService && (
            <p className="text-xs text-red-600">Đơn phải có ít nhất một sản phẩm / dịch vụ.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderItemsEditor;
